import type { ReactNode } from "react";
import { AppHeader } from "./AppHeader";
import { ConnectivityToast } from "./ConnectivityToast";
import { PausedScreen } from "./PausedScreen";
import { SeatBanner } from "./SeatBanner";
import { StorageStatusBanner } from "./StorageStatusBanner";
import { TourProvider } from "../tour/TourProvider";
import { TourOverlay } from "../tour/TourOverlay";
import { PresenceProvider } from "../presence/PresenceContext";
import { usePresenceHeartbeat } from "../presence/usePresenceHeartbeat";
import { useServicePaused } from "../paused";

/** The heartbeat has to sit inside <PresenceProvider> to publish its counts,
 *  and outside the paused screen: a paused tab holds no seat. */
function HeartbeatMount(): null {
  usePresenceHeartbeat();
  return null;
}

export function AuthedLayout({ children }: { children: ReactNode }): JSX.Element {
  const paused = useServicePaused();
  if (paused) return <PausedScreen />;
  return (
    <PresenceProvider>
      <HeartbeatMount />
      <TourProvider>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            position: "relative",
          }}
        >
          <AppHeader />
          <StorageStatusBanner />
          {/* Seat presence (spec §5.5): shows only while the team is over its
              included seats, and never offline. */}
          <SeatBanner />
          {/* `overflowY: auto` is load-bearing: the shared viewer stylesheet
              (/vendor/styles.css) sets `html, body { overflow: hidden }` for the
              full-bleed HDD shell, and that reaches every cloud route too. The
              scroll container therefore has to be here, or a flights list taller
              than the viewport has no way to reach its lower rows. The viewer
              route sizes itself to this box exactly, so it never gets a bar. */}
          <main
            style={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflowY: "auto",
            }}
          >
            {children}
          </main>
          {/* Transient bottom bar shown on online/offline transitions. Lives at the
              layout root so it floats over every authed page. */}
          <ConnectivityToast />
          {/* Guided tour (spec 2026-09-04). Renders nothing while inactive. */}
          <TourOverlay />
        </div>
      </TourProvider>
    </PresenceProvider>
  );
}
